// client/src/components/PlanC/PostPublishPrompt/index.tsx
//
// TRIL-116: post-publish modal per spec §346. References the just-published
// post's primary topic. 3-strike suppression via priorDismissCount.

import * as Dialog from '@radix-ui/react-dialog';
import * as React from 'react';
import { trackEvent } from '../../../helpers/analytics';
import { humanize } from '../../../helpers/interestsTaxonomy';
import './post-publish-prompt.scss';

interface Props {
  postTopic: string; // Interests enum key (underscored)
  priorDismissCount: number;
  contextFilled: boolean;
  onDismiss: () => Promise<void> | void;
  onCtaClick: () => void;
}

const MAX_DISMISSALS = 3;

export function PostPublishPrompt({
  postTopic,
  priorDismissCount,
  contextFilled,
  onDismiss,
  onCtaClick,
}: Props): React.ReactElement | null {
  const visible = !contextFilled && priorDismissCount < MAX_DISMISSALS;

  React.useEffect(() => {
    if (visible) {
      trackEvent('plan_c.prompt.shown', {
        post_topic: postTopic,
        total_prior_dismissals: priorDismissCount,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, postTopic]);

  if (!visible) return null;

  const handleDismiss = (): void => {
    trackEvent('plan_c.prompt.dismissed', {
      post_topic: postTopic,
      total_dismiss_count: priorDismissCount + 1,
    });
    void onDismiss();
  };

  const handleCta = (): void => {
    trackEvent('plan_c.prompt.cta_clicked', { post_topic: postTopic });
    onCtaClick();
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && handleDismiss()}>
      <Dialog.Portal>
        <Dialog.Overlay className="plan-c-prompt__overlay" />
        <Dialog.Content className="plan-c-prompt__content">
          <Dialog.Title className="plan-c-prompt__title">
            Want to boost your post's credibility?
          </Dialog.Title>
          <Dialog.Description className="plan-c-prompt__body">
            URG (User Reputation Generator) can rate your expertise more
            accurately if it knows a bit about your background. About 30
            seconds.
          </Dialog.Description>
          <p className="plan-c-prompt__topic">
            Topic of your post: <strong>{humanize(postTopic)}</strong> — did you
            study or work in this area?
          </p>
          <div className="plan-c-prompt__actions">
            <button
              type="button"
              onClick={handleCta}
              className="plan-c-prompt__button-primary"
            >
              Tell URG about my background
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="plan-c-prompt__button-secondary"
            >
              Maybe later
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
